import { Controller } from '@hotwired/stimulus'

export default class extends Controller<HTMLDivElement> {
  static targets = ['file', 'fileAction', 'template', 'input']

  declare readonly fileTarget: HTMLSpanElement
  declare readonly fileActionTarget: HTMLSpanElement
  declare readonly templateTarget: HTMLTemplateElement
  declare readonly inputTarget: HTMLInputElement

  static values = {
    isMultiple: Boolean
  }

  declare readonly isMultipleValue: boolean

  // MARK: internal helpers

  #renderFileFromTemplate(fileName: string): void {
    const clone = this.templateTarget.content.cloneNode(true) as any
    const newHTML = clone.firstElementChild.innerHTML.replace(':file_name', fileName)
    clone.firstElementChild.innerHTML = newHTML
    clone.firstElementChild.dataset.fileName = fileName
    this.fileTarget.appendChild(clone)
  }

  #updateFileDisplay(files: FileList | null): void {
    if (files === null) return
    const fileNames = Array.from(files).map(f => f.name)

    if (this.isMultipleValue) {
      this.fileActionTarget.innerText = 'Click to replace files'
      this.fileTarget.innerHTML = ''
      fileNames.forEach(name => {
        this.#renderFileFromTemplate(name)
      })
    } else {
      const name = fileNames[0]
      if (name === undefined) return
      this.fileActionTarget.innerText = 'Click to replace file'

      this.fileTarget.innerHTML = ''
      this.#renderFileFromTemplate(name)
    }
  }

  #fileWrapperElementForName(fileName: string): HTMLDivElement | null {
    type FileElements = NodeListOf<HTMLDivElement>
    const allFileElements = this.fileTarget.querySelectorAll('file-element') as FileElements
    for (const fileElement of allFileElements) {
      if (fileElement.dataset.fileName === fileName) {
        return fileElement
      }
    }
    return null
  }

  // MARK: click event

  clicked(e: MouseEvent): void {
    console.log('CLICKED', e.target, this.element)
    if (e.target !== this.element) return
    this.inputTarget?.click()
  }

  // MARK: drag and drop events

  dragged(e: DragEvent): void {
    e.preventDefault()
    e.stopPropagation()
  }

  draggedIn(e: DragEvent): void {
    e.preventDefault()
    e.stopPropagation()
    this.element.classList.add('border-4', 'bg-200')
  }

  draggedOut(e: DragEvent): void {
    e.preventDefault()
    e.stopPropagation()
    this.element.classList.remove('border-4', 'bg-200')
  }

  dropped(e: DragEvent): void {
    e.preventDefault()
    e.stopPropagation()
    this.element.classList.remove('border-4', 'bg-200')
    const input = this.element.querySelector('input')
    const files = e.dataTransfer?.files
    if (input === null || files === undefined) return
    input.files = files
    this.#updateFileDisplay(files)
  }

  // MARK: normal events

  pickFile(e: InputEvent): void {
    const input = e.target as HTMLInputElement

    this.#updateFileDisplay(input.files)
  }

  keyPressed(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.element.querySelector('input')?.click()
    }
  }

  // MARK: direct upload events

  directUploadStarted(e: CustomEvent<{ file: File }>): void {
    const fileElement = this.#fileWrapperElementForName(e.detail.file.name)
    fileElement?.querySelector('progress')?.classList.remove('hidden')
    fileElement?.querySelector('progress-percentage')?.classList.remove('hidden')
  }

  directUploadProgressed(e: CustomEvent<{ file: File; progress: number }>): void {
    const fileElement = this.#fileWrapperElementForName(e.detail.file.name)
    if (fileElement === null) return

    const progressBar = fileElement.querySelector('progress')
    if (progressBar === null) return

    progressBar.value = e.detail.progress
    const progressPercentage = fileElement.querySelector('progress-percentage')
    if (progressPercentage === null) return

    progressPercentage.innerHTML = `${Math.floor(e.detail.progress)}%`
  }

  directUploadErrored(e: CustomEvent<{ file: File; error: string }>): void {
    const fileElement = this.#fileWrapperElementForName(e.detail.file.name)
    const errorElement = fileElement?.nextElementSibling
    if (errorElement === null || errorElement === undefined) return

    errorElement.classList.remove('hidden')
    errorElement.innerHTML = errorElement.innerHTML.replace(':error', e.detail.error)
    e.preventDefault()
  }

  directUploadEnded(e: CustomEvent<{ file: File }>): void {
    const fileElement = this.#fileWrapperElementForName(e.detail.file.name)
    fileElement?.querySelector('progress')?.classList.add('hidden')
    fileElement?.querySelector('progress-percentage')?.classList.add('hidden')
    fileElement?.querySelector('progress-done-icon')?.classList.remove('hidden')
  }

  clearFiles(): void {
    this.inputTarget.value = ''
    this.inputTarget.disabled = false
    this.fileTarget.innerHTML = ''
    this.fileActionTarget.innerText = this.isMultipleValue
      ? '(no files selected)'
      : '(no file selected)'

    const form = this.element.closest('form')
    if (form) {
      form
        .querySelectorAll<HTMLInputElement>(`input[type="hidden"][name="${this.inputTarget.name}"]`)
        .forEach(el => el.remove())
    }
  }
}
